/**
 * Bancada de vozes.
 *
 * ## Por que existe
 *
 * Trocar a voz do Will exigia editar `/etc/will-voice.env`, reiniciar o
 * serviço e ter acesso à máquina. Comparar duas vozes significava fazer isso
 * duas vezes; comparar a mesma voz em dois estados afetivos, quatro. Na
 * prática ninguém comparava, e a voz padrão continuou sendo a que estava lá
 * desde o primeiro dia — não porque era a melhor, mas porque era a instalada.
 *
 * ## O que ela mostra, e por que
 *
 * Cada voz vem com a **latência medida** ao lado. Sem isso a escolha é feita
 * só por gosto, e a diferença entre a mais rápida e a mais lenta do catálogo é
 * de 576 ms — perceptível numa conversa, invisível numa audição isolada.
 *
 * O seletor de estado existe porque a prosódia varia por atividade: a mesma
 * voz em `awaiting` termina em pergunta, em `worried` respira mais. Ouvir a
 * mesma frase nos dois é a única forma de julgar se a camada de pontuação
 * ajuda ou atrapalha.
 *
 * ## Dois áudios, não cinco modos
 *
 * O seletor de modo oferecia os cinco nomes do catálogo (`realtime`,
 * `balanced`, `precision`, `quality`, `batch`). Medido: quatro devolvem o mesmo
 * arquivo, byte a byte, e só `realtime` difere — e a latência não separa nenhum
 * deles (as cinco medianas cabem em 58 ms, contra uma cauda de ~1.250 ms dentro
 * de um mesmo modo). Cinco alavancas para dois comportamentos é o defeito que
 * o `speed` do OmniVoice já cobrou deste projeto: uma alavanca que mente é pior
 * que uma que não existe.
 *
 * Agora são duas: **Padrão** e **Alternativo**. Os nomes de implementação saem
 * do seletor e ficam na linha de baixo, como prova da afirmação e como pista
 * para quem for depurar um cliente antigo — não como coisas a escolher. Quem
 * decide quais modos entram em cada família é o servidor, via `audioDistinto`;
 * a redução mora em `voice/modos.ts` e é calculada, nunca escrita à mão.
 *
 * ## O que ela deliberadamente NÃO faz
 *
 * Não permite voz por atividade. A identidade é uma decisão de produto — uma
 * voz, um modo —, e esta tela é a bancada para tomá-la ouvindo, não a licença
 * para cada tela ter a sua.
 *
 * Não reescreve sozinha a escolha de quem já tinha um dos quatro nomes salvos.
 * Ela continua valendo, continua soando igual, e a tela diz isso com todas as
 * letras — trocar por baixo seria decidir pela pessoa em silêncio, que é
 * exatamente o que se está corrigindo aqui.
 */

import { useCallback, useEffect, useState } from 'react';
import { DOME, type DomeState } from '@will/contracts';
import { authFetch } from '../auth';
import {
  carregarCatalogo,
  escolhaAtual,
  escolher,
  usarPadraoDoServidor,
  type Catalogo,
} from '../voice/escolha';
import { familiaDe, pendenciaDa, reduzirModos } from '../voice/modos';

/** Frase de teste. Tem vírgula no meio e conectivo: exercita respiro e pausa. */
const FRASE =
  'Olha, o relatório do trimestre ficou pronto na quinta à tarde, mas o time ainda não revisou tudo.';

/**
 * Estados oferecidos para comparação.
 *
 * Não são os catorze: só os que têm perfil de prosódia não-neutro mudam alguma
 * coisa. Oferecer os outros seria convidar a ouvir duas vezes o mesmo áudio e
 * concluir que a camada não funciona.
 */
const ESTADOS: DomeState[] = ['speaking', 'thinking', 'worried', 'awaiting', 'completed'];

export function VozBancada(): JSX.Element {
  const [catalogo, setCatalogo] = useState<Catalogo | null>(null);
  const [escolha, setEscolha] = useState(escolhaAtual());
  const [estado, setEstado] = useState<DomeState>('speaking');
  const [tocando, setTocando] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    void carregarCatalogo().then(setCatalogo);
  }, []);

  const ouvir = useCallback(
    async (vozId: string) => {
      setErro(null);
      setTocando(vozId);
      try {
        // Ouvir uma voz aqui é uma síntese real, cobrada na conta do titular
        // como qualquer outra: vai com a credencial da sessão. O catálogo
        // (`carregarCatalogo`) continua anônimo — ele não sintetiza nada.
        const r = await authFetch('/api/tts', {
          method: 'POST',
          headers: { 'content-type': 'application/json', accept: 'audio/ogg' },
          body: JSON.stringify({
            text: FRASE,
            voice_id: vozId,
            state: estado,
            ...(escolha.modo === null ? {} : { mode: escolha.modo }),
            ...(escolha.idioma === null ? {} : { language: escolha.idioma }),
          }),
        });
        if (!r.ok) throw new Error(`o servidor respondeu ${r.status}`);
        const url = URL.createObjectURL(await r.blob());
        const audio = new Audio(url);
        // Revogar no fim: sem isto cada audição vaza um blob, e uma sessão de
        // comparação são dezenas.
        audio.onended = () => {
          URL.revokeObjectURL(url);
          setTocando(null);
        };
        audio.onerror = () => {
          URL.revokeObjectURL(url);
          setTocando(null);
          setErro('o navegador não conseguiu tocar o áudio');
        };
        await audio.play();
      } catch (e) {
        setTocando(null);
        setErro(e instanceof Error ? e.message : 'falhou');
      }
    },
    [escolha.modo, estado],
  );

  if (catalogo === null) {
    return (
      <fieldset className="u1-panel__group">
        <legend className="u1-panel__legend">Voz</legend>
        <p className="u1-vb__vazio">carregando o catálogo…</p>
      </fieldset>
    );
  }

  const emUso = escolha.vozId ?? catalogo.padrao;

  // O catálogo é a fonte de quantos comportamentos existem. `null` aqui quer
  // dizer "este servidor não me contou quais modos mudam o áudio" — e nesse
  // caso a tela mostra a lista crua em vez de fingir uma escolha entre dois.
  const seletorDeAudio = reduzirModos(catalogo);
  const familia = familiaDe(escolha.modo, catalogo);
  const pendencia =
    seletorDeAudio === null
      ? ({ tipo: 'nenhuma' } as const)
      : pendenciaDa(escolha.modo, catalogo, seletorDeAudio);

  return (
    <fieldset className="u1-panel__group">
      <legend className="u1-panel__legend">Voz</legend>

      <p className="u1-vb__nota">
        A mesma frase em cada voz, com a latência medida ao lado. A voz, o idioma e o áudio
        escolhidos valem para o Will de verdade e sobrevivem à recarga.
      </p>

      <div className="u1-vb__estado">
        <span className="u1-vb__rotulo">Comparar como</span>
        <div className="u1-vb__chips" role="radiogroup" aria-label="Estado para comparação">
          {ESTADOS.map((e) => (
            <button
              key={e}
              type="button"
              role="radio"
              aria-checked={e === estado}
              className={`u1-vb__chip${e === estado ? ' u1-vb__chip--on' : ''}`}
              onClick={() => setEstado(e)}
            >
              {DOME[e].label}
            </button>
          ))}
        </div>
      </div>

      <ul className="u1-vb__lista">
        {catalogo.vozes.map((v) => {
          const ativa = v.id === emUso;
          return (
            <li key={v.id} className={`u1-vb__item${ativa ? ' u1-vb__item--on' : ''}`}>
              <button
                type="button"
                className="u1-vb__ouvir"
                onClick={() => void ouvir(v.id)}
                aria-label={`Ouvir ${v.rotulo}`}
                disabled={tocando !== null}
              >
                {tocando === v.id ? '▮▮' : '▶'}
              </button>
              <span className="u1-vb__nome">{v.rotulo}</span>
              <span className="u1-vb__feitio">{v.feitio}</span>
              <span className="u1-vb__ms">{v.msMedido} ms</span>
              <button
                type="button"
                className={`u1-vb__usar${ativa ? ' u1-vb__usar--on' : ''}`}
                onClick={() => setEscolha(escolher({ vozId: v.id }))}
                aria-pressed={ativa}
              >
                {ativa ? 'em uso' : 'usar'}
                {/* Sem isto são nove botões chamados "usar" para quem navega
                    por leitor de tela: o nome da voz só existia na coluna ao
                    lado, que o botão não carrega consigo. */}
                <span className="u1-oculto"> {v.rotulo}</span>
              </button>
            </li>
          );
        })}
      </ul>

      <div className="u1-vb__rodape">
        {/* O idioma vem ANTES do modo porque é decisão de produto e o modo é
            de desempenho: quem abre esta bancada para definir a língua padrão
            do Will não deveria ter que passar por uma opção de inferência
            primeiro. */}
        <label className="u1-vb__modo">
          <span className="u1-vb__rotulo">Idioma</span>
          <select
            value={escolha.idioma ?? ''}
            onChange={(ev) =>
              setEscolha(escolher({ idioma: ev.target.value === '' ? null : ev.target.value }))
            }
          >
            <option value="">padrão ({catalogo.idiomaPadrao})</option>
            {catalogo.idiomas.map((i) => (
              <option key={i.id} value={i.id}>
                {i.rotulo}
              </option>
            ))}
          </select>
        </label>

        {/* "Áudio", e não "Modo": modo é o nome do parâmetro que vai no corpo
            da requisição, e `realtime`/`balanced` são nomes de implementação.
            O que a pessoa escolhe é qual dos dois áudios existentes ela vai
            ouvir — dois é o número medido, não um arredondamento. */}
        {seletorDeAudio !== null ? (
          <div className="u1-vb__modo">
            <span className="u1-vb__rotulo">Áudio</span>
            <div className="u1-vb__chips" role="radiogroup" aria-label="Áudio da voz">
              {/* Mesmas fichas do seletor de estado, de propósito: são duas
                  opções curtas, e uma lista de dois numa caixa de seleção
                  esconde metade da escolha atrás de um toque. */}
              <button
                type="button"
                role="radio"
                aria-checked={familia === 'padrao'}
                className={`u1-vb__chip${familia === 'padrao' ? ' u1-vb__chip--on' : ''}`}
                onClick={() => setEscolha(escolher({ modo: seletorDeAudio.padrao }))}
              >
                Padrão
                <span className="u1-oculto"> — o áudio que o Will já usa</span>
              </button>
              <button
                type="button"
                role="radio"
                aria-checked={familia === 'alternativo'}
                className={`u1-vb__chip${familia === 'alternativo' ? ' u1-vb__chip--on' : ''}`}
                onClick={() => setEscolha(escolher({ modo: seletorDeAudio.alternativo }))}
              >
                Alternativo
                <span className="u1-oculto"> — o único que devolve arquivo diferente</span>
              </button>
            </div>
          </div>
        ) : (
          // Servidor que não publica `audioDistinto` — inclusive o que estiver
          // no ar antes de o catálogo novo subir. Aqui a redução seria um chute
          // com cara de fato, então a lista vai crua, do jeito que o servidor a
          // conta. Feia e verdadeira é melhor que arrumada e inventada.
          <label className="u1-vb__modo">
            <span className="u1-vb__rotulo">Modo</span>
            <select
              value={escolha.modo ?? ''}
              onChange={(ev) =>
                setEscolha(escolher({ modo: ev.target.value === '' ? null : ev.target.value }))
              }
            >
              <option value="">padrão ({catalogo.modoPadrao})</option>
              {catalogo.modos.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.id}
                  {m.audioDistinto === false ? ' · mesmo áudio dos demais' : ''}
                  {m.aviso ? ` · ${m.aviso}` : ''}
                </option>
              ))}
            </select>
          </label>
        )}

        <button
          type="button"
          className="u1-vb__reset"
          onClick={() => setEscolha(usarPadraoDoServidor())}
        >
          voltar ao padrão
          {/* Este botão limpa os três, e o texto visível não diz qual. */}
          <span className="u1-oculto"> de voz, idioma e áudio</span>
        </button>
      </div>

      {seletorDeAudio === null && (
        <p className="u1-vb__nota">
          Este servidor não informa quais modos mudam o áudio, então a lista vai como ele a conta.
          Medido em 17/08 no catálogo atual: quatro dos cinco modos devolvem o mesmo arquivo, byte
          a byte.
        </p>
      )}

      {seletorDeAudio !== null && (
        // A prova fica na tela, não só no commit: sem ela "Alternativo" seria
        // mais um rótulo pedindo confiança. Os nomes de implementação aparecem
        // aqui, em prosa, porque é o que faz o log do servidor e um cliente
        // velho em cache serem depuráveis — e não porque haja o que escolher
        // entre eles.
        <p className="u1-vb__nota">
          São dois áudios, não cinco modos: {seletorDeAudio.identicos.join(', ')} devolvem o mesmo
          arquivo, byte a byte, e só {seletorDeAudio.alternativo} difere. A latência não separa
          nenhum deles — a variação dentro de um mesmo modo é maior que a diferença entre eles.
          Escolha pelo ouvido: o ▶ acima toca com o áudio marcado aqui.
          {seletorDeAudio.aviso !== undefined ? ` ${seletorDeAudio.aviso}` : ''}
        </p>
      )}

      {pendencia.tipo !== 'nenhuma' && (
        // Uma escolha antiga não vira estado inválido nem troca sozinha. Ela
        // continua sendo enviada — e quem a fez fica sabendo por que sumiu da
        // lista, com a normalização a um toque.
        <p className="u1-vb__nota">
          {pendencia.tipo === 'aposentado'
            ? `A escolha salva neste navegador é ${pendencia.modo}, que saiu da lista por devolver o mesmo arquivo do padrão, byte a byte. Ela continua valendo e continua sendo enviada — nada no que você ouve mudou.`
            : `A escolha salva neste navegador é ${pendencia.modo}, que não está no catálogo deste servidor. Ele descarta o que não reconhece e fala com o padrão dele, então a escolha hoje é decorativa.`}{' '}
          <button
            type="button"
            className="u1-vb__reset"
            onClick={() =>
              setEscolha(escolher({ modo: seletorDeAudio === null ? null : seletorDeAudio.padrao }))
            }
          >
            usar Padrão
            <span className="u1-oculto"> — só o áudio; a voz e o idioma ficam como estão</span>
          </button>
        </p>
      )}

      {erro !== null && (
        <p className="u1-vb__erro" role="alert">
          {erro}
        </p>
      )}
    </fieldset>
  );
}

export default VozBancada;
